/** iOS-style page building blocks (Working Hours, Analytics): page header, inset-grouped panel, stat tile. */
import type { ReactNode } from 'react';
import { cn } from '../../utils/cn';

const SURFACE = 'bg-white ring-1 ring-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.04)]';

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.02em] text-gray-900 text-balance">{title}</h1>
        {subtitle && <p className="mt-1 text-[13px] text-gray-500 text-pretty">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>}
    </header>
  );
}

export function Panel({ title, subtitle, actions, className, children }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section className={cn('rounded-2xl p-5', SURFACE, className)}>
      {(title || actions) && (
        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900 text-balance">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-[13px] text-gray-500 text-pretty">{subtitle}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/** `raised` stands alone on the page; `inset` sits inside a Panel (gray fill, no second card edge). */
export function StatTile({ label, value, unit, sub, tone, variant = 'raised', className }: {
  label: string;
  value: ReactNode;
  unit?: string;
  sub?: ReactNode;
  tone?: 'good' | 'bad';
  variant?: 'raised' | 'inset';
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col rounded-xl p-4', variant === 'raised' ? SURFACE : 'bg-gray-50', className)}>
      <span className="text-[12px] font-medium text-gray-500">{label}</span>
      <span className={cn('mt-1.5 text-[26px] font-semibold leading-none tracking-[-0.02em] text-gray-900',
        tone === 'good' && 'text-emerald-600', tone === 'bad' && 'text-red-600')}>
        {value}
        {unit && <span className="ml-1 text-[13px] font-medium tracking-normal text-gray-500">{unit}</span>}
      </span>
      {sub && <span className="mt-2 text-[12px] leading-snug text-gray-500 text-pretty">{sub}</span>}
    </div>
  );
}
